import { useEffect, useMemo, useRef, useState } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';

import { getVisibleQuestionsForQuotaGroup } from '@api/instant_survey';
import type { Group, InstantSurvey, Question, QuestionLogic } from '@api/instant_survey/types';
import { GATES } from '@constants';

import { getSurveyLanguages, type Item, type PreviewSelection } from './helpers';
import { useCanUseSurveyTranslation } from '../../../utilities/hooks';

interface UseSurveyPreviewParams {
  languageInfo?: InstantSurvey['languageInfo'];
  questionLogic?: Record<string, QuestionLogic>;
  questions?: Question[];
  quotaGroups?: Group[];
}

const isPreviewableQuestion = (question: Question) =>
  question?.properties?.isLanguageScreener !== true;

/**
 * Checks whether the current user can preview surveys.
 */
export const useCanPreviewSurveys = () => {
  const { client } = useStatsigClient();
  return client.checkGate(GATES.PREVIEW_SURVEY);
};

/**
 * Consolidates the state and handlers needed to render survey previews.
 * The selected quota group controls which questions are available in the
 * preview dropdown, and changing quota groups resets the preview to that
 * group's first visible question.
 */
export const useSurveyPreview = ({
  languageInfo,
  questionLogic,
  questions,
  quotaGroups,
}: UseSurveyPreviewParams) => {
  const canUseSurveyTranslation = useCanUseSurveyTranslation();
  const { languages, sourceLanguage } = useMemo(
    () => getSurveyLanguages(canUseSurveyTranslation ? languageInfo : undefined),
    [canUseSurveyTranslation, languageInfo]
  );
  const hasMultipleLanguages = languages.length > 1;
  const previewableQuestions = useMemo(() => questions?.filter(isPreviewableQuestion), [questions]);

  const initialPreviewSelection = getFirstPreviewSelection({
    questionLogic,
    questions: previewableQuestions,
    quotaGroups,
  });
  const { previewSelection, resetPreviewSelection, setPreviewSelection } = usePreviewSelection({
    language: hasMultipleLanguages ? sourceLanguage : undefined,
    questionId: initialPreviewSelection.questionId,
    quotaGroupId: initialPreviewSelection.quotaGroupId,
  });

  const previewQuestions = useMemo(
    () =>
      getVisibleQuestionsForQuotaGroup({
        questionLogic,
        questions: previewableQuestions,
        quotaGroupId: previewSelection.quotaGroupId,
      }),
    [previewableQuestions, previewSelection.quotaGroupId, questionLogic]
  );

  const handleUpdatePreviewQuotaGroup = (quotaGroupId: number) => {
    const nextQuestions = getVisibleQuestionsForQuotaGroup({
      questionLogic,
      questions: previewableQuestions,
      quotaGroupId,
    });

    setPreviewSelection((prev) => {
      return {
        ...prev,
        questionId: nextQuestions[0]?.id,
        quotaGroupId,
      };
    });
  };

  const handleUpdatePreviewQuestion = (questionId: number) => {
    setPreviewSelection((prev) => {
      return {
        ...prev,
        ...getPreviewSelectionForQuestion({
          currentQuotaGroupId: prev.quotaGroupId,
          questionId,
          questionLogic,
          questions: previewableQuestions,
          quotaGroups,
        }),
      };
    });
  };

  const handleUpdatePreviewLanguage = (language: string) => {
    setPreviewSelection((prev) => {
      return {
        ...prev,
        language,
      };
    });
  };

  const resetPreviewQuestion = () => {
    const nextQuestionId = previewQuestions[0]?.id;
    if (nextQuestionId === undefined) return;

    setPreviewSelection((prev) => {
      return {
        ...prev,
        questionId: nextQuestionId,
        restartKey: (prev.restartKey ?? 0) + 1,
      };
    });
  };

  const [showPreviewModal, handleClosePreviewModal, handlePreviewButtonClick] = useShowPreviewModal(
    {
      resetSelection: resetPreviewSelection,
      updateQuestion: handleUpdatePreviewQuestion,
    }
  );
  useValidPreviewSelection({
    languages,
    questionLogic,
    questions: previewableQuestions,
    quotaGroups,
    setPreviewSelection,
    sourceLanguage,
  });
  return {
    handleClosePreviewModal,
    handlePreviewButtonClick,
    handleUpdatePreviewLanguage,
    handleUpdatePreviewQuestion,
    handleUpdatePreviewQuotaGroup,
    languages,
    previewQuestions,
    previewSelection,
    resetPreviewQuestion,
    showPreviewModal,
  };
};

/**
 * Owns the active preview selection and keeps the latest valid initial selection
 * available for modal close/reset.
 */
const usePreviewSelection = ({ language, questionId, quotaGroupId }: PreviewSelection) => {
  const latestInitialPreviewSelectionRef = useRef<PreviewSelection>({
    language,
    questionId,
    quotaGroupId,
    restartKey: 0,
  });
  const [previewSelection, setPreviewSelection] = useState<PreviewSelection>(
    latestInitialPreviewSelectionRef.current
  );

  useEffect(() => {
    latestInitialPreviewSelectionRef.current = {
      language,
      questionId,
      quotaGroupId,
      restartKey: 0,
    };
  }, [language, questionId, quotaGroupId]);

  const resetPreviewSelection = () => setPreviewSelection(latestInitialPreviewSelectionRef.current);

  return {
    previewSelection,
    resetPreviewSelection,
    setPreviewSelection,
  };
};

/**
 * Controls preview modal visibility. Opening from a question action can update the
 * selected question before showing the modal; general opens and closes reset to the
 * latest initial selection.
 */
const useShowPreviewModal = ({
  resetSelection,
  updateQuestion,
}: {
  resetSelection: () => void;
  updateQuestion: (questionId: number) => void;
}) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    resetSelection();
  };

  // Reset preview selection before each open so previous modal state is not reused.
  const handlePreviewButtonClick = (questionId?: number) => {
    resetSelection();
    if (typeof questionId === 'number') updateQuestion(questionId);
    setShowPreviewModal(true);
  };

  return [showPreviewModal, handleClosePreviewModal, handlePreviewButtonClick] as const;
};

/**
 * Repairs stale preview selections after question, logic, quota group, or language
 * changes. Quota group validity is resolved first, then the question is validated
 * against the questions visible for that quota group, and finally the language is
 * validated against the survey's available languages.
 */
const useValidPreviewSelection = ({
  languages,
  questionLogic,
  questions,
  quotaGroups,
  setPreviewSelection,
  sourceLanguage,
}: UseSurveyPreviewParams & {
  languages: string[];
  setPreviewSelection: React.Dispatch<React.SetStateAction<PreviewSelection>>;
  sourceLanguage: string;
}) => {
  const hasMultipleLanguages = languages.length > 1;
  const defaultLanguage = hasMultipleLanguages ? sourceLanguage : undefined;

  useEffect(() => {
    if (!questions || !quotaGroups) return;

    setPreviewSelection((prev) => {
      const getVisibleQuestions = (quotaGroupId?: number) =>
        getVisibleQuestionsForQuotaGroup({
          questionLogic,
          questions,
          quotaGroupId,
        });

      if (!prev) {
        return {
          language: defaultLanguage,
          ...getFirstPreviewSelection({ questionLogic, questions, quotaGroups }),
        };
      }

      const { language, questionId, quotaGroupId } = prev;
      const quotaGroupIsValid = checkIfValid(quotaGroups, quotaGroupId);
      const nextQuestions = quotaGroupIsValid ? getVisibleQuestions(quotaGroupId) : [];
      const questionIsValid = checkIfValid(nextQuestions, questionId);
      const languageIsValid = hasMultipleLanguages
        ? language !== undefined && languages.includes(language)
        : language === undefined;

      if (questionIsValid && quotaGroupIsValid && languageIsValid) return prev;

      const nextLanguage = languageIsValid ? language : defaultLanguage;

      if (quotaGroupIsValid && nextQuestions.length) {
        return {
          ...prev,
          language: nextLanguage,
          questionId: questionIsValid ? questionId : nextQuestions[0]?.id,
          quotaGroupId,
        };
      }

      return {
        ...prev,
        ...getFirstPreviewSelection({ questionLogic, questions, quotaGroups }),
        language: nextLanguage,
      };
    });
  }, [
    defaultLanguage,
    hasMultipleLanguages,
    languages,
    questionLogic,
    questions,
    quotaGroups,
    setPreviewSelection,
  ]);
};

const checkIfValid = (items: Item[], id?: number) => {
  if (id === undefined || id === null) return false;
  return items.some((item) => String(item.id) === String(id));
};

const getFirstPreviewSelection = ({
  questionLogic,
  questions,
  quotaGroups,
}: UseSurveyPreviewParams): Pick<PreviewSelection, 'questionId' | 'quotaGroupId'> => {
  const getVisibleQuestions = (quotaGroupId?: number) =>
    getVisibleQuestionsForQuotaGroup({
      questionLogic,
      questions,
      quotaGroupId,
    });
  const quotaGroup = quotaGroups?.find((group) => getVisibleQuestions(group.id).length);
  const quotaGroupId = quotaGroup?.id ?? quotaGroups?.[0]?.id;

  return {
    questionId: getVisibleQuestions(quotaGroupId)[0]?.id,
    quotaGroupId,
  };
};

/**
 * Preserve a direct question preview when possible. If the requested question is hidden for the
 * current quota group, select the first quota group where that question is visible.
 */
const getPreviewSelectionForQuestion = ({
  currentQuotaGroupId,
  questionId,
  questionLogic,
  questions,
  quotaGroups,
}: UseSurveyPreviewParams & {
  currentQuotaGroupId?: number;
  questionId: number;
}): Pick<PreviewSelection, 'questionId' | 'quotaGroupId'> => {
  const currentQuotaGroupQuestions = getVisibleQuestionsForQuotaGroup({
    questionLogic,
    questions,
    quotaGroupId: currentQuotaGroupId,
  });
  const questionIsVisibleForCurrentQuotaGroup = checkIfValid(
    currentQuotaGroupQuestions,
    questionId
  );

  if (questionIsVisibleForCurrentQuotaGroup) {
    return {
      questionId,
      quotaGroupId: currentQuotaGroupId,
    };
  }

  const firstVisibleQuotaGroup = quotaGroups?.find((quotaGroup) =>
    getVisibleQuestionsForQuotaGroup({
      questionLogic,
      questions,
      quotaGroupId: quotaGroup.id,
    }).some((question) => String(question.id) === String(questionId))
  );

  return {
    questionId: firstVisibleQuotaGroup ? questionId : currentQuotaGroupQuestions[0]?.id,
    quotaGroupId: firstVisibleQuotaGroup?.id ?? currentQuotaGroupId,
  };
};
